// The hall-browser registration. Own flow table, no mailbox.
//
// Not imported into the public transition table. start → form on /j/ stays
// impossible. After Submit: thank-you, home in five seconds, hidden
// triple-click top left.
//
// `/s/` runs plain Registration and `/sw/` runs the onsite wheel: attract,
// details, terms, optional survey, then spin. Join kind does not choose between
// them. The emailed-code wheel lives on `/w/`, not here.

import { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_WHEEL_COPY } from "@nova/xp-visitor";
import { HomeReset, IdleHome } from "../../ui/src/home/HomeReset.js";
import { BUILD } from "./build/config.js";
import { readEnvironment } from "./build/environment.js";
import { Checking } from "./checking.js";
import type { AgreementDocument } from "../../ui/src/legal/documents.js";
import {
  fetchConsentWording,
  serverFor,
  submitOnsiteRegistration,
  type Refusal,
  type Transport,
} from "./flow.js";
import { WheelAfter } from "./wheelAfter.js";
import { visitorLogoFrom, WheelFrame } from "./wheelFrame.js";
import { recordMeasuredPage } from "./measurement.js";
import { onsiteMayFollow, type OnsiteStage } from "./onsite.js";
import { Page } from "./page.js";
import { readConsentWording, readNoticeConfig, type ConsentWordingReading } from "./privacy/notices.js";
import {
  legalDocumentsForPage,
  RegistrationSteps,
  type FormPage,
} from "./registrationForm.js";
import { ClientMark } from "./clientMark.js";
import { applyClientTokens, applyTheme, preferredTheme, type PageTheme } from "./theme.js";

const environment = readEnvironment(BUILD.environment);
const server = serverFor(environment);
const baseUrl = "baseUrl" in server ? server.baseUrl : null;

export function OnsiteApp({
  activationId,
  transport,
  wheelJoin,
}: {
  activationId: string;
  transport: Transport;
  /** Set by `/s/` versus `/sw/`, never by the activation's preferred QR. */
  wheelJoin: boolean;
}) {
  const [theme, setTheme] = useState<PageTheme>(() => preferredTheme());
  const [heldForm, setHeldForm] = useState<FormPage>("details");
  const [stage, setStage] = useState<OnsiteStage>(() =>
    activationId === ""
      ? {
          at: "unusable",
          problem:
            "This onsite address does not name a stand. Open it from the console, or use the tablet.",
        }
      : { at: "start" },
  );
  const [busy, setBusy] = useState(false);
  const [spinTicket, setSpinTicket] = useState<string | null>(null);
  const [wording, setWording] = useState<ConsentWordingReading | null>(null);

  useEffect(() => {
    if (wording?.ok === true) applyClientTokens(theme, wording.theme, wording.themeDark);
    else applyTheme(theme);
  }, [theme, wording]);

  const go = useCallback((next: OnsiteStage) => {
    setStage((current) => {
      if (!onsiteMayFollow(current.at, next.at)) {
        throw new Error(
          `refusing to move from ${current.at} to ${next.at}: onsite.ts does not permit it. ` +
            `If this is a transition the hall page should have, it belongs in ONSITE_TRANSITIONS.`,
        );
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const at = stage.at;
    if (at === "writing") return;
    recordMeasuredPage(wheelJoin ? "onsite-wheel" : "onsite", at, activationId);
  }, [stage.at, wheelJoin, activationId]);

  const notices = useMemo(() => readNoticeConfig(BUILD.notices), []);

  useEffect(() => {
    if (baseUrl === null || activationId === "") return undefined;
    let live = true;
    void (async () => {
      const got = await fetchConsentWording(transport, baseUrl, activationId, "onsite");
      const reading: ConsentWordingReading = got.ok
        ? await readConsentWording(got.served, { keepMailbox: true })
        : { ok: false, problem: got.refusal.problem };
      if (live) setWording(reading);
    })();
    return () => {
      live = false;
    };
  }, [activationId, transport]);

  // Filled once the stand's documents are known, below. Until then the
  // footer lists none rather than linking away from the tablet.
  let footDocuments: readonly AgreementDocument[] = [];
  const shell = (children: React.ReactNode) => (
    <Page
      theme={theme}
      onTheme={setTheme}
      fullscreen
      lane="onsite"
      onsiteFooter
      footDocuments={footDocuments}
    >
      {children}
    </Page>
  );

  if (baseUrl === null) {
    return shell(
      <section>
        <h1>Not available</h1>
        <p>{"problem" in server ? server.problem : "This build names no server."}</p>
      </section>,
    );
  }

  if (wording === null) {
    return shell(
      <Checking />,
    );
  }

  if (!wording.ok) {
    return shell(
      <section>
        <h1>Not available</h1>
        <p>
          This registration page is not finished being set up, so it is not collecting anything. Please
          ask somebody on the stand.
        </p>
        <p role="note">{wording.problem}</p>
      </section>,
    );
  }

  if (wording.captureOffer === "public") {
    return shell(
      <section>
        <h1>Not available</h1>
        <p>
          This stand is taking registrations on a visitor&apos;s own phone, not at this onsite address.
          Scan the public code, or ask somebody on the stand.
        </p>
      </section>,
    );
  }

  if (!notices.ok) {
    return shell(
      <section>
        <h1>Not available</h1>
        <p>
          This registration page is not finished being set up, so it is not collecting anything. Please
          ask somebody on the stand.
        </p>
        <p role="note">{notices.problem}</p>
      </section>,
    );
  }

  const legal = legalDocumentsForPage(wording.legalDocuments, notices.documents);
  footDocuments = legal;
  const wheelLogo = visitorLogoFrom(wording.logos, theme);

  if (stage.at === "done" && wheelJoin && spinTicket !== null) {
    return shell(
      <WheelAfter
        activationId={activationId}
        ticket={spinTicket}
        slices={wording.wheelSlices}
        lane="onsite"
        mode={theme}
        transport={transport}
        baseUrl={baseUrl}
        logo={wheelLogo}
        crewResetEnabled={wording.onsiteCrewResetEnabled}
        onHome={() => go({ at: "start" })}
      />,
    );
  }

  const stageBody =
    stage.at === "unusable" ? (
      <section>
        <h1>Nothing to register for</h1>
        <p>{stage.problem}</p>
      </section>
    ) : stage.at === "refused" ? (
      <section aria-live="assertive">
        <h2>That did not work</h2>
        <p>{stage.problem}</p>
        <button type="button" className="xp-btn" onClick={() => go({ at: "start" })}>
          Start again
        </button>
      </section>
    ) : stage.at === "done" ? (
      <section aria-live="polite">
        <HomeReset
          onHome={() => go({ at: "start" })}
          crewResetEnabled={wording.onsiteCrewResetEnabled}
        />
        <h1>Thank You.</h1>
        <p className="web-lede">You are registered. There is nothing else to do.</p>
        <p className="web-said">
          This screen returns to the start in a few seconds, ready for the next person.
        </p>
      </section>
    ) : stage.at === "start" ? (
      <section className="web-welcome">
        {wheelJoin ? null : (
          <ClientMark
            logos={wording.logos}
            theme={theme}
            baseUrl={baseUrl}
            fallback={null}
          />
        )}
        {/* The hand-off's welcome is the mark and Start. The page still has its
            heading, for a screen reader, without drawing one. */}
        <h1 className={wheelJoin ? undefined : "sr"}>{wheelJoin ? DEFAULT_WHEEL_COPY.heading : "Register"}</h1>
        {wheelJoin ? <p>{DEFAULT_WHEEL_COPY.body}</p> : null}
        <div className="xp-acts">
          <button type="button" className="xp-btn xp-btn--primary" onClick={() => go({ at: "details" })}>
            {wheelJoin ? DEFAULT_WHEEL_COPY.attract : "Start"}
          </button>
        </div>
      </section>
    ) : (
      <RegistrationSteps
        lede={null}
        onsite
        busy={busy || stage.at === "writing"}
        consent={wording.documents}
        attestations={wording.attestations}
        privacyNotice={wording.privacyNotice}
        fields={wording.fields}
        legal={legal}
        keepMailbox={true}
        foldDisplayName={wording.foldDisplayName}
        step={
          stage.at === "details" || stage.at === "terms" || stage.at === "survey"
            ? stage.at
            : heldForm
        }
        onStep={(next) => {
          setHeldForm(next);
          go({ at: next });
        }}
        onLeaveDetails={() => go({ at: "start" })}
        onSubmit={async (details) => {
          go({ at: "writing" });
          setBusy(true);
          const outcome = await submitOnsiteRegistration(transport, baseUrl, {
            activation_id: activationId,
            lane: wheelJoin ? "wheel" : "registration",
            display_name: details.displayName === "" ? null : details.displayName,
            phone: details.phone === "" ? null : details.phone,
            config_version: wording.configVersion,
            field_set_version: wording.fieldSetVersion,
            fields: details.fields,
            consent: details.consent,
          });
          setBusy(false);
          if (outcome.ok) {
            setSpinTicket(outcome.written.spin_ticket ?? null);
            go({ at: "done" });
            return;
          }
          go({ at: "refused", ...outcome.refusal });
        }}
      />
    );

  // Everywhere with something on screen that nobody is coming back to finish.
  // `start` has nothing to clear, `writing` has an answer on its way, and
  // `done` already carries its own shorter clock.
  const idle =
    stage.at === "details" || stage.at === "terms" || stage.at === "survey" || stage.at === "refused" ? (
      <IdleHome
        onHome={() => go({ at: "start" })}
        afterMs={wording.onsiteIdleHomeMs}
        crewResetEnabled={wording.onsiteCrewResetEnabled}
      />
    ) : null;

  if (wheelJoin && stage.at !== "unusable" && stage.at !== "refused") {
    return shell(
      <WheelFrame slices={wording.wheelSlices} mode={theme} lane="onsite" logo={wheelLogo}>
        {idle}
        {stageBody}
      </WheelFrame>,
    );
  }

  return shell(
    <>
      {idle}
      {stageBody}
    </>,
  );
}

export type { Refusal };
